<template>
	<v-dialog
		v-model="showDialog"
		max-width="960px"
		:fullscreen="$vuetify.breakpoint.xs ? true : false"
		:transition="$vuetify.breakpoint.xs ? 'scroll-y-reverse-transition' : ''"
	>
		<v-card :tile="!$vuetify.breakpoint.xs">
			<v-app-bar dense dark flat color="primary">
				<v-app-bar-title class="color-white">配車依頼書情報</v-app-bar-title>
				<v-spacer />
				<v-btn icon dark @click="showDialog = false" color="white">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-app-bar>
			<v-sheet elevation="0" class="pa-2">
				<v-row no-gutters justify="end">
					<v-btn
						color="secondary"
						v-if="delivery_order.status_type === 'sent' || delivery_order.status_type === 'pending'"
						@click="$router.push({ name: 'delivery_order_show', params: { id } })"
					>
						<v-icon class="mr-1"> mdi-note-text-outline</v-icon>
						配車依頼書表示
					</v-btn>
					<v-btn
						class="ml-2"
						color="secondary"
						v-if="
							authority_flag && delivery_order.status_type === 'draft' && delivery_order.send_by_fax
						"
						@click="sendDeliveryOrder('sent')"
					>
						<v-icon class="mr-1"> mdi-send</v-icon>
						配車依頼書作成
					</v-btn>
					<v-btn
						class="ml-2"
						color="secondary"
						v-if="
							authority_flag &&
							delivery_order.status_type === 'draft' &&
							!delivery_order.send_by_fax
						"
						@click="sendDeliveryOrder('pending')"
					>
						<v-icon class="mr-1"> mdi-send</v-icon>
						確認依頼送信
					</v-btn>
					<v-btn
						class="ml-2"
						color="secondary"
						v-if="authority_flag"
						@click="$router.push({ name: 'delivery_order_edit', params: { id: id } })"
					>
						<v-icon class="mr-1"> mdi-pencil</v-icon>
						配車依頼データ編集
					</v-btn>
				</v-row>

				<template v-if="confirmation_authority_flag && delivery_order.status_type === 'pending'">
					<v-row class="mt-4" no-gutters justify="center">
						<v-alert max-width="600px" outlined type="warning" color="red" prominent>
							<p class="text-body-2 mx-sm-4 mx-2">
								この配車依頼書は、<strong>確認待ち</strong>となっています。
								<br />
								確認、または、却下を行ってください。
							</p>
							<v-row class="mt-4" no-gutters justify="space-around">
								<v-btn color="secondary" @click="confirmDeliveryOrder()">
									<v-icon class="mr-1"> mdi-check-circle-outline</v-icon>
									確認
								</v-btn>
								<v-menu v-model="menu" :close-on-content-click="false">
									<template v-slot:activator="{ on, attrs }">
										<v-btn color="red" dark v-bind="attrs" v-on="on">
											<v-icon class="mr-1">mdi-alert-circle-outline</v-icon>
											却下
										</v-btn>
									</template>

									<v-card width="400px" class="top-border-menu top-border-menu--red">
										<v-form ref="form" v-model="valid" class="pa-2">
											<v-textarea
												class="mt-4"
												v-model="reason"
												label="却下理由"
												:rules="[rules.required]"
												hint="却下理由は作成者へメッセージとして送信されます。"
												outlined
												required
											/>
										</v-form>
										<v-card-actions class="ma-0">
											<v-row no-gutters justify="space-around">
												<v-btn
													color="red"
													:dark="valid"
													@click="rejectDeliveryOrder()"
													:disabled="!valid"
												>
													<v-icon class="mr-1">mdi-alert-circle-outline</v-icon>
													却下
												</v-btn>
												<v-btn text @click="menu = false"> キャンセル </v-btn>
											</v-row>
										</v-card-actions>
									</v-card>
								</v-menu>
							</v-row>
						</v-alert>
					</v-row>
				</template>
				<div class="mt-4">
					<table width="100%" class="show-dialog-table">
						<tbody>
							<ShowDetailRecord :columnName="'注文番号'" :value="formattedOrderNumber()" />
							<ShowDetailRecord
								:columnName="'金額'"
								:value="`${Number(delivery_order.price).toLocaleString()}円`"
							/>
							<ShowDetailRecord :columnName="'状態'" :value="delivery_order.status_type_i18n" />
							<ShowDetailRecord
								:columnName="'FAXで送信'"
								:value="delivery_order.send_by_fax ? '◯' : '×'"
							/>
							<ShowDetailRecord
								:columnName="'注文日'"
								:value="common.formatDate(delivery_order.order_date)"
							/>
							<ShowDetailRecord :columnName="'運送会社'" :value="delivery_order.supplier.name" />
							<ShowDetailRecord :columnName="'担当者'" :value="delivery_order.pic_name" />
							<ShowDetailRecord
								:columnName="'集荷場所'"
								:value="delivery_order.shipping_place.name"
							/>
							<ShowDetailRecord :columnName="'備考'" :value="delivery_order.memo" text />
							<ShowDetailRecord :columnName="'発注者'" :value="delivery_order.user.full_name" />
							<ShowDetailRecord
								v-if="!delivery_order.send_by_fax"
								:columnName="'確認者'"
								:value="delivery_order.confirmor"
							/>
							<ShowDetailRecord
								v-if="delivery_order.quote_request"
								:columnName="'関連見積番号'"
								:value="delivery_order.quote_request.quote_number"
							/>
							<ShowDetailRecord
								:columnName="'作成日'"
								:value="common.formatDateTime(delivery_order.created_at)"
							/>
							<ShowDetailRecord
								:columnName="'最終更新日'"
								:value="common.formatDateTime(delivery_order.updated_at)"
							/>
						</tbody>
						<tbody v-for="(car, i) in delivery_order.delivery_order_cars" :key="`car-${i}`">
							<tr height="8px" />
							<ShowDetailRecord
								:columnName="`車種${i + 1}`"
								:value="''"
								:thStyle="{ backgroundColor: 'white' }"
								:tdStyle="{ border: '0 transparent none' }"
							/>
							<ShowDetailRecord :columnName="'車種'" :value="car.car_model" />
							<ShowDetailRecord :columnName="'台数/式'" :value="car.number_of_cars" />
							<ShowDetailRecord
								:columnName="'単価'"
								:value="Number(car.price).toLocaleString() + '円'"
							/>

						</tbody>

						<tbody v-for="(info, i) in infosByAddress" :key="i">
							<tr height="8px" />
							<ShowDetailRecord
								:columnName="`納入先${i + 1}`"
								:value="''"
								:thStyle="{ backgroundColor: 'white' }"
								:tdStyle="{ border: '0 transparent none' }"
							/>
							<ShowDetailRecord :columnName="'製造番号'" :value="''">
								<template v-slot:value>
									<a
										class="d-block"
										v-for="(orderIndividualInfo, i) in info.infos"
										:key="i"
										@click="openOrderIndividualInfoDialog(orderIndividualInfo.id)"
									>
										{{ common.productNameWithProductNumber(orderIndividualInfo) }}
									</a>
								</template>
							</ShowDetailRecord>
							<ShowDetailRecord :columnName="'郵便番号'" :value="info.delivery_info.zipcode" />
							<ShowDetailRecord :columnName="'都道府県'" :value="info.delivery_info.prefecture" />
							<ShowDetailRecord :columnName="'市郡町村'" :value="info.delivery_info.city" />
							<ShowDetailRecord :columnName="'住所'" :value="info.delivery_info.address" text />
							<ShowDetailRecord :columnName="'会社名'" :value="info.delivery_info.company_name" />
							<ShowDetailRecord
								:columnName="'客先担当者'"
								:value="info.delivery_info.person_name"
							/>
							<ShowDetailRecord :columnName="'電話番号'" :value="info.delivery_info.phone" />
							<ShowDetailRecord :columnName="'FAX'" :value="info.delivery_info.fax" />
							<ShowDetailRecord
								:columnName="'集荷日'"
								:value="common.formatDate(info.delivery_info.shipping_date)"
							/>
							<ShowDetailRecord
								:columnName="'客先納入日'"
								:value="
									common.formatDateWithNullableTime(info.delivery_info, 'deadline', 'deadline_time')
								"
							/>
						</tbody>
						<tbody v-for="(mailInfo, i) in delivery_order.mail_infos" :key="`mail-${i}`">
							<tr height="8px" />
							<ShowDetailRecord
								:columnName="`メール情報${i + 1}`"
								:value="''"
								:thStyle="{ backgroundColor: 'white' }"
								:tdStyle="{ border: '0 transparent none' }"
							/>
							<ShowDetailRecord :columnName="'メールアドレス'" :value="mailInfo.mail_address" />
							<ShowDetailRecord
								:columnName="'CCメールアドレス'"
								:value="mailInfo.cc_mail_address"
							/>
							<ShowDetailRecord :columnName="'件名'" :value="mailInfo.title" />
							<ShowDetailRecord :columnName="'内容'" :value="mailInfo.content" text />
							<ShowDetailRecord
								:columnName="'作成日時'"
								:value="mailInfo.created_at"
								:dataType="'datetime'"
							/>
							<ShowDetailRecord :columnName="'添付ファイル'" :value="''">
								<template v-slot:value>
									<div
										v-for="(file, index) in filteredFiles(mailInfo.return_all_attached_file_infos)"
										:key="`file-${index}`"
									>
										<v-btn
											small
											outlined
											class="ma-1 btn-no-transform"
											color="primary"
											:href="file.url"
											target="_blank"
										>
											<v-icon class="mr-1">mdi-download</v-icon>
											{{ file.name }}</v-btn
										>
									</div>
								</template>
							</ShowDetailRecord>
						</tbody>
					</table>
				</div>
			</v-sheet>
		</v-card>
		<OrderIndividualInfoShowDialog
			:id="selectedOrderIndividualInfoId"
			:dialog.sync="orderIndividualInfoDialog"
		/>
	</v-dialog>
</template>

<script>
import axios from "axios"
import errorHandling from "../../../helper/errorHandling"
import authenticate from "../../../helper/authenticate"
import common from "../../../helper/common"
import rules from "../../../helper/rules"
import ShowDetailRecord from "../../shared/ShowDetailRecord.vue"
import OrderIndividualInfoShowDialog from "../../order_individual_info/components/OrderIndividualInfoShowDialog.vue"

export default {
	props: {
		dialog: {
			type: Boolean,
			required: false,
			default: false,
		},
		id: {
			type: null,
			required: false,
		},
	},
	components: {
		ShowDetailRecord,
		OrderIndividualInfoShowDialog,
	},
	data() {
		return {
			authority_flag: false,
			confirmation_authority_flag: false,
			delivery_order: {
				price: null,
				status_type: null,
				status_type_i18n: null,
				order_date: null,
				number_of_cars: null,
				car_model: null,
				supplier: {
					name: null,
				},
				pic_name: null,
				shipping_place: { name: null },
				memo: null,
				user: {
					full_name: null,
				},
				quote_request: {
					quote_number: null,
				},
				created_at: null,
				updated_at: null,
				delivery_order_individual_infos: [],
				delivery_order_cars: [],
				mail_infos: [],
			},
			selectedOrderIndividualInfoId: null,
			orderIndividualInfoDialog: false,
			menu: false,
			reason: null,
			valid: false,
			common,
			rules,
		}
	},
	methods: {
		openDialog() {
			this.showDialog = true
		},
		fetchDeliveryOrder(id) {
			const headers = authenticate.setHeader()
			const url = `/erp/v1/delivery_orders/${id}`
			axios
				.get(url, { headers })
				.then((res) => {
					this.showDialog = true
					this.delivery_order = res.data.delivery_order
					this.authority_flag = res.data.authority_flag
					this.confirmation_authority_flag = res.data.confirmation_authority_flag
				})
				.catch((err) => {
					errorHandling.errorResponse(err)
				})
		},
		sendDeliveryOrder(status_type) {
			const headers = authenticate.setHeader()
			const url = `/erp/v1/delivery_orders/${this.id}/`
			axios
				.put(url, { delivery_order: { status_type } }, { headers })
				.then((res) => {
					let message
					if (status_type == "pending") {
						message = "配車依頼書を作成し、確認依頼メッセージを送信しました。"
					} else if (status_type == "sent") {
						message = "配車依頼書を作成しました。"
					}
					this.$store.dispatch("setFlash", {
						type: "notice",
						text: message,
					})

					this.$router.push({ name: "delivery_order_show", params: { id: res.data.id } })
				})
				.catch((err) => {
					errorHandling.errorResponse(err)
				})
		},
		formattedOrderNumber() {
			if (this.delivery_order.sub_number) {
				return `${this.delivery_order.order_number}-${String(
					this.delivery_order.sub_number
				).padStart(3, "0")}`
			} else {
				return this.delivery_order.order_number
			}
		},
		openOrderIndividualInfoDialog(id) {
			this.selectedOrderIndividualInfoId = id
			this.orderIndividualInfoDialog = true
		},
		confirmDeliveryOrder() {
			const headers = authenticate.setHeader()
			const url = `/erp/v1/delivery_orders/${this.id}/confirmations`
			axios
				.post(url, {}, { headers })
				.then((res) => {
					this.$store.dispatch("setFlash", {
						type: "notice",
						text: "配車依頼書を確認しました。",
					})
					this.delivery_order.status_type = res.data.status_type
					this.delivery_order.status_type_i18n = res.data.status_type_i18n
					this.delivery_order.confirmor = res.data.confirmor
					this.delivery_order.order_number = res.data.order_number
					this.delivery_order.sub_number = res.data.sub_number
					this.$emit("reload-delivery-orders")
				})
				.catch((err) => {
					errorHandling.errorResponse(err)
				})
		},
		rejectDeliveryOrder() {
			const headers = authenticate.setHeader()
			const url = `/erp/v1/delivery_orders/${this.id}/confirmations`
			axios
				.delete(url, { params: { reason: this.reason }, headers })
				.then((res) => {
					this.$store.dispatch("setFlash", {
						type: "notice",
						text: "配車依頼書を却下しました。",
					})
					this.delivery_order.status_type = res.data.status_type
					this.delivery_order.status_type_i18n = res.data.status_type_i18n
					this.$emit("reload-delivery-orders")
					this.menu = false
				})
				.catch((err) => {
					errorHandling.errorResponse(err)
				})
		},
		filteredFiles(attachedFiles) {
			return attachedFiles.filter((file) => !file.destroy_flag)
		},
	},
	computed: {
		infosByAddress() {
			return this.delivery_order.delivery_order_individual_infos.reduce((groups, current) => {
				const group = groups.find(
					(group) => group.full_address === `${current.prefecture}${current.city}${current.address}`
				)
				if (group) {
					group.infos.push(current.order_individual_info)
				} else {
					groups.push({
						full_address: `${current.prefecture}${current.city}${current.address}`,
						delivery_info: {
							zipcode: current.zipcode,
							prefecture: current.prefecture,
							city: current.city,
							address: current.address,
							fax: current.fax,
							phone: current.phone,
							company_name: current.company_name,
							person_name: current.person_name,
							deadline: current.deadline,
							shipping_date: current.shipping_date,
						},
						infos: [current.order_individual_info],
					})
				}
				return groups
			}, [])
		},
		showDialog: {
			get() {
				return this.dialog
			},
			set(newVal) {
				return this.$emit("update:dialog", newVal)
			},
		},
	},
	watch: {
		id(newVal, oldVal) {
			if (newVal == oldVal) return
			this.fetchDeliveryOrder(newVal)
		},
		deep: true,
	},
}
</script>